import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Presentational-only pieces documenting the real design tokens from
 * globals.css (CSS variables) and tailwind.config.js — not components the
 * app renders elsewhere, just the /design "Foundations" reference.
 */

const COLOR_TOKENS: { name: string; swatchClassName: string }[] = [
  { name: "background", swatchClassName: "bg-background border" },
  { name: "foreground", swatchClassName: "bg-foreground" },
  { name: "card", swatchClassName: "bg-card border" },
  { name: "popover", swatchClassName: "bg-popover border" },
  { name: "primary", swatchClassName: "bg-primary" },
  { name: "secondary", swatchClassName: "bg-secondary" },
  { name: "muted", swatchClassName: "bg-muted" },
  { name: "accent", swatchClassName: "bg-accent" },
  { name: "destructive", swatchClassName: "bg-destructive" },
  { name: "border", swatchClassName: "bg-border" },
  { name: "input", swatchClassName: "bg-input" },
  { name: "ring", swatchClassName: "bg-ring" },
  { name: "success", swatchClassName: "bg-success" },
  { name: "warning", swatchClassName: "bg-warning" },
  { name: "error", swatchClassName: "bg-error" },
  { name: "info", swatchClassName: "bg-info" },
];

export function ColorSwatches() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      {COLOR_TOKENS.map((token) => (
        <div key={token.name} className="space-y-2">
          <div className={cn("h-14 w-full rounded-md", token.swatchClassName)} />
          <p className="text-sm font-medium">{token.name}</p>
        </div>
      ))}
    </div>
  );
}

// Tailwind's default spacing scale is `n * 0.25rem`; showing a representative
// subset matches how it's actually used across the app's className props.
const SPACING_STEPS = [1, 2, 3, 4, 6, 8, 10, 12, 16, 20, 24];

export function SpacingScale() {
  return (
    <div className="space-y-3">
      {SPACING_STEPS.map((step) => (
        <div key={step} className="flex items-center gap-4">
          <span className="w-24 shrink-0 text-sm text-muted-foreground">
            {step} · {step * 0.25}rem
          </span>
          <div
            className="h-4 rounded-sm bg-primary"
            style={{ width: `${step * 0.25}rem` }}
          />
        </div>
      ))}
    </div>
  );
}

const RADIUS_TOKENS = [
  { name: "sm", className: "rounded-sm" },
  { name: "md", className: "rounded-md" },
  { name: "lg", className: "rounded-lg" },
  { name: "full", className: "rounded-full" },
];

export function RadiusScale() {
  return (
    <div className="flex flex-wrap gap-6">
      {RADIUS_TOKENS.map((radius) => (
        <div key={radius.name} className="space-y-2 text-center">
          <div className={cn("h-16 w-16 border-2 border-primary bg-muted", radius.className)} />
          <p className="text-sm text-muted-foreground">{radius.name}</p>
        </div>
      ))}
    </div>
  );
}

const BORDER_WIDTHS = [
  { name: "1px (default)", className: "border" },
  { name: "2px", className: "border-2" },
  { name: "4px", className: "border-4" },
];

export function BorderWidths() {
  return (
    <div className="flex flex-wrap gap-6">
      {BORDER_WIDTHS.map((border) => (
        <div key={border.name} className="space-y-2 text-center">
          <div
            className={cn("h-16 w-16 rounded-md border-primary bg-muted", border.className)}
          />
          <p className="text-sm text-muted-foreground">{border.name}</p>
        </div>
      ))}
    </div>
  );
}
